// we sometimes have internal links as absolute URLs instead of linking to an entry
export const isAbsoluteInternalLink = (link: string): boolean => {
  const normalizedLink = link
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '');
  return normalizedLink.startsWith('compassion.com/');
};

export const isAnchorLink = (link: string): boolean => {
  if (!link || !link.trim()) {
    return false;
  }
  const trimmedLink = link.trim();
  return trimmedLink.startsWith('#');
};

export const isInternalLink = (link: string, siteDomain?: string): boolean => {
  if (!link || !link.trim()) {
    return false;
  }

  if (isAnchorLink(link)) {
    return false;
  }

  // Array of paths that should be treated as external when on compassion.com domain
  // Add new paths here in the future as needed
  const internalPathsForExternalUse = [
    '/account',
    '/my-account',
    '/blog/tag',
    '/search/',
    '/press/press-releases/tag',
    '/crisis-updates/tag',
    // Examples of potential future additions:
    // '/checkout/',
    // '/payment/',
    // '/login/',
    // Allow-listed for legacy (Note: As we migrate these pages, remove them from the list)
    '/catalog/order.htm',
    '/christian-speakers/request-a-speaker.htm',
    '/donate/matching-gifts.htm',
    '/estate-and-asset-giving/wills-trusts-and-estates.htm',
    '/estate-and-asset-giving/wills-trusts-and-estates-form.htm',
    '/mobile-app/connect-with-your-sponsored-child',
    '/prayer-partner-signup.htm',
    '/prayer.htm',
    '/sms-terms-and-conditions.htm',
    '/sponsor_a_child/select-a-child-for-me',
    '/sponsor-a-child/find-your-match',
    '/untojesus',
    '/world-days/world-kindness-day.htm',
  ];

  // Normalize the link to check for compassion.com paths
  const normalizedLink = link
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '');

  // Check specifically if the URL starts with compassion.com followed by any external path
  if (
    internalPathsForExternalUse.some((path) =>
      normalizedLink.startsWith(`compassion.com${path}`),
    )
  ) {
    return false;
  }

  if (link.startsWith('/')) {
    return true;
  }

  const hostname =
    siteDomain
      ?.replace(/^www\./, '')
      .replace(/^skippy\.devint\./, '')
      .replace(/^skippy\.stage\./, '')
      .replace(/^skippy\.compassion\./, 'compassion.') || 'compassion.com';

  if (
    link.startsWith(hostname) ||
    link.split('?')[0].includes(`/${hostname}`) ||
    link.split('?')[0].includes(`www.${hostname}`)
  ) {
    return true;
  }

  try {
    const urlHostname = new URL(`https://${normalizedLink}`).hostname;
    return urlHostname === hostname;
  } catch {
    return false;
  }
};

export const isMailToLink = (link: string): boolean => {
  if (!link || !link.trim()) {
    return false;
  }
  const trimmedLink = link.trim();
  return (
    trimmedLink.toLowerCase().includes('mailto:') ||
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(trimmedLink)
  );
};

// Helper function to validate phone numbers more strictly
const isValidPhoneNumber = (phoneNumber: string): boolean => {
  if (!phoneNumber || !phoneNumber.trim()) {
    return false;
  }

  const trimmed = phoneNumber.trim();

  // Check for invalid characters that suggest it's not a phone number
  // Allow only digits, spaces, hyphens, dots, parentheses, and + at the beginning
  const validCharsPattern = /^[+]?[()\d\s.-]+$/;
  if (!validCharsPattern.test(trimmed)) {
    return false;
  }

  // Check for patterns that suggest it's not a phone number
  // Reject if it contains letters (except in tel: prefix which is already handled)
  if (/[a-zA-Z]/.test(trimmed)) {
    return false;
  }

  // Reject if it contains @ (email-like)
  if (trimmed.includes('@')) {
    return false;
  }

  // Reject if it looks like a URL
  if (trimmed.includes('://') || trimmed.includes('www.')) {
    return false;
  }

  // Reject if it has extensions or additional text
  if (/x\d+$|ext\d+$|extension\d+$/i.test(trimmed)) {
    return false;
  }

  // Remove all non-digit characters except + at the beginning
  const digitsOnly = trimmed.replace(/[^\d+]/g, '');

  // Must be at least 10 digits long (excluding +)
  const phoneDigits = digitsOnly.replace(/^\+/, '');
  if (phoneDigits.length < 10 || phoneDigits.length > 15) {
    return false;
  }

  const phonePatterns = [
    /^[+]?1?[2-9]\d{2}[2-9]\d{2}\d{4}$/, // US format: +1234567890, 1234567890, 2345678901
    /^[+]?\d{10,15}$/, // International format: 10-15 digits
  ];

  // Check if the digits match a valid phone pattern
  const digitsMatch = phonePatterns.some((pattern) => pattern.test(digitsOnly));

  // Also check formatted US numbers, but be more strict
  const formattedUSPattern =
    /^[+]?1?[-.\s]?[(]?\d{3}[)]?[-.\s]?\d{3}[-.\s]?\d{4}$/;
  const formattedMatch = formattedUSPattern.test(trimmed);

  return digitsMatch || formattedMatch;
};

export const isTelLink = (link: string): boolean => {
  if (!link || !link.trim()) {
    return false;
  }
  const trimmedLink = link.trim();

  // If it starts with tel:, check what comes after
  if (trimmedLink.toLowerCase().startsWith('tel:')) {
    const phoneNumber = trimmedLink.slice(4); // Remove 'tel:' prefix
    return isValidPhoneNumber(phoneNumber);
  }

  return isValidPhoneNumber(trimmedLink);
};
export const isValidExternalUrl = (
  link: string,
  siteDomain?: string,
): boolean => {
  if (!link || !link.trim()) {
    return false;
  }
  const trimmedLink = link.trim();

  // @TODO we should do some catching for non-prod urls... E.g. *.cloudfront.net , skippy.devint.compassion.com
  // @TODO: TikTok urls contain "@", we'll need to account for this

  return (
    trimmedLink.match(
      /^https:\/\/[a-zA-Z0-9-]+([-.]{1}[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}/,
    ) !== null &&
    trimmedLink.match(/^(?![^/]*@).*$/) !== null &&
    !isInternalLink(trimmedLink, siteDomain) &&
    !isAnchorLink(trimmedLink) &&
    !isMailToLink(trimmedLink) &&
    !isTelLink(trimmedLink)
  );
};

export const isUrlFromTrustedDomain = (
  url: string,
  domains: string[],
): boolean => {
  try {
    const urlObj = new URL(url);
    return domains.some(
      (domain) =>
        urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`),
    );
  } catch {
    return false;
  }
};

export const validateUrl = (input: string) => {
  if (!URL.canParse(input) || !input?.startsWith('https://')) {
    return null;
  }

  return new URL(input);
};
